import './Sidebar.scss'
import {
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject
} from 'react'
import {
  MdOutlineExpandMore,
  MdOutlineMenu,
  MdOutlineMenuOpen,
  MdOutlineOpenInNew
} from 'react-icons/md'
import { Link, useLocation } from 'react-router-dom'
import {
  clx,
  IconButton,
  observeMovement,
  Portal,
  pushEscape,
  rectOf,
  Tooltip,
  VisuallyHidden
} from '../../ui'
import { isLinkActive, isNavGroup, type NavGroupItem, type NavItem, type NavLinkItem } from '../nav'
import { useRailExpanded, useSidebarExpanded } from './useRailExpanded'

/**
 * The three forms of ADR 0012. AppShell picks one from the viewport width.
 * `sidebar` and `rail` both collapse to icons (ADR 0019); `drawer` never does.
 */
export type SidebarForm = 'sidebar' | 'rail' | 'drawer'

export interface SidebarProps {
  /** Already filtered by RBAC and resolved for unported areas by the integrator. */
  items: NavItem[]
  /** Shown as `v{version}` under the pinned items. */
  version: string
  form?: SidebarForm
  /**
   * Controlled expanded state, for a toggle that lives outside the sidebar
   * (AppShell's top bar). Omitted, the sidebar keeps its own remembered state.
   */
  isExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /** Whether the collapse toggle sits at the top of the sidebar itself. */
  hasToggle?: boolean
  id?: string
  className?: string
}

const EXTERNAL_SUFFIX = ' (opens portal)'

/**
 * `<nav aria-label="Main">`: the portal's menu (CustomLayout.tsx:62-175).
 * Labels always stay in the accessibility tree; when collapsed they
 * are visually hidden and shown as tooltips.
 */
export function Sidebar({
  items,
  version,
  form = 'sidebar',
  isExpanded: controlledExpanded,
  onExpandedChange,
  hasToggle = true,
  id,
  className
}: SidebarProps) {
  const { pathname } = useLocation()
  const rail = useRailExpanded()
  const docked = useSidebarExpanded()
  // The rail and the docked sidebar both collapse to icons, each with its own
  // remembered state; the drawer always shows labels.
  const isCollapsible = form !== 'drawer'
  const [ownExpanded, setOwnExpanded] = form === 'rail' ? rail : docked
  const isExpanded = controlledExpanded ?? ownExpanded
  const setExpanded = onExpandedChange ?? setOwnExpanded
  const isCompact = isCollapsible && !isExpanded
  const top = items.filter((item) => isNavGroup(item) || !item.isPinned)
  const pinned = items.filter((item): item is NavLinkItem => !isNavGroup(item) && !!item.isPinned)

  const render = (item: NavItem) =>
    isNavGroup(item) ? (
      <SidebarGroup key={item.id} item={item} pathname={pathname} isCompact={isCompact} />
    ) : (
      <li key={item.id} className="sidebar__item">
        <SidebarLink item={item} pathname={pathname} isCompact={isCompact} />
      </li>
    )

  return (
    <nav
      id={id}
      aria-label="Main"
      className={clx('sidebar', `sidebar--as-${form}`, className)}
      data-state={isCollapsible ? (isExpanded ? 'expanded' : 'collapsed') : undefined}
    >
      {isCollapsible && hasToggle && (
        <div className="sidebar__toggle">
          <IconButton
            label={isExpanded ? 'Collapse navigation' : 'Expand navigation'}
            aria-expanded={isExpanded}
            icon={isExpanded ? <MdOutlineMenuOpen /> : <MdOutlineMenu />}
            hasTooltip={false}
            className="sidebar__toggle-button"
            onClick={() => {
              setExpanded(!isExpanded)
            }}
          />
        </div>
      )}
      <ul className="sidebar__list">{top.map(render)}</ul>
      <div className="sidebar__footer">
        {pinned.length > 0 && <ul className="sidebar__list">{pinned.map(render)}</ul>}
        <p className="sidebar__version">v{version}</p>
      </div>
    </nav>
  )
}

interface ItemProps {
  pathname: string
  isCompact: boolean
}

function Label({ isCompact, children }: { isCompact: boolean; children: ReactNode }) {
  return isCompact ? (
    <VisuallyHidden>{children}</VisuallyHidden>
  ) : (
    <span className="sidebar__label">{children}</span>
  )
}

function SidebarLink({ item, pathname, isCompact }: ItemProps & { item: NavLinkItem }) {
  const content = (
    <>
      {item.icon && (
        <span className="sidebar__icon" aria-hidden="true">
          {item.icon}
        </span>
      )}
      <Label isCompact={isCompact}>{item.label}</Label>
      {item.isExternal && !isCompact && (
        <span className="sidebar__external" aria-hidden="true">
          <MdOutlineOpenInNew />
        </span>
      )}
    </>
  )

  // Unported areas open in the same tab (ADR 0017): a plain link, not the
  // router. The name carries the "opens portal" suffix (aria-label rather
  // than hidden text, so no name computation drops the space).
  const link = item.isExternal ? (
    <a href={item.href} className="sidebar__link" aria-label={`${item.label}${EXTERNAL_SUFFIX}`}>
      {content}
    </a>
  ) : (
    <Link
      to={item.href}
      className="sidebar__link"
      aria-current={isLinkActive(item, pathname) ? 'page' : undefined}
    >
      {content}
    </Link>
  )

  if (!isCompact) return link
  return (
    <Tooltip
      content={item.isExternal ? `${item.label}${EXTERNAL_SUFFIX}` : item.label}
      side="bottom"
      isLabel
    >
      {link}
    </Tooltip>
  )
}

/**
 * The "Status" category: a disclosure whose panel is headed "Upgrade
 * process". Open by default (`initialOpen`). Collapsed, the sidebar stays
 * collapsed and the button opens the children in a flyout beside it
 * (ADR 0021).
 */
function SidebarGroup({ item, pathname, isCompact }: ItemProps & { item: NavGroupItem }) {
  const id = useId()
  const panelId = `sidebar-panel-${id}`
  const titleId = `sidebar-panel-title-${id}`
  const flyoutId = `sidebar-flyout-${id}`
  const [isOpen, setOpen] = useState(true)
  const [isFlyoutOpen, setFlyoutOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isShown = isOpen && !isCompact
  const hasActive = item.children.some((child) => isLinkActive(child, pathname))

  // A flyout belongs to the collapsed form only, and closes on navigation.
  const [lastPath, setLastPath] = useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    if (isFlyoutOpen) setFlyoutOpen(false)
  }
  if (!isCompact && isFlyoutOpen) setFlyoutOpen(false)

  return (
    <li className="sidebar__item">
      <button
        ref={buttonRef}
        type="button"
        className="sidebar__link"
        aria-expanded={isCompact ? isFlyoutOpen : isShown}
        aria-controls={isCompact ? (isFlyoutOpen ? flyoutId : undefined) : panelId}
        data-active={hasActive ? 'true' : undefined}
        onClick={() => {
          if (isCompact) setFlyoutOpen(!isFlyoutOpen)
          else setOpen(!isOpen)
        }}
      >
        {item.icon && (
          <span className="sidebar__icon" aria-hidden="true">
            {item.icon}
          </span>
        )}
        <Label isCompact={isCompact}>{item.label}</Label>
        {!isCompact && (
          <span className="sidebar__chevron" aria-hidden="true">
            <MdOutlineExpandMore />
          </span>
        )}
      </button>
      <div id={panelId} className="sidebar__panel" hidden={!isShown}>
        <p id={titleId} className="sidebar__panel-title">
          {item.title ?? item.label}
        </p>
        <ul className="sidebar__list" aria-labelledby={titleId}>
          {item.children.map((child) => (
            <li key={child.id} className="sidebar__item">
              <SidebarLink item={child} pathname={pathname} isCompact={false} />
            </li>
          ))}
        </ul>
      </div>
      {isCompact && isFlyoutOpen && (
        <SidebarFlyout
          id={flyoutId}
          anchorRef={buttonRef}
          title={item.title ?? item.label}
          items={item.children}
          pathname={pathname}
          onClose={(returnFocus) => {
            setFlyoutOpen(false)
            if (returnFocus) buttonRef.current?.focus()
          }}
        />
      )}
    </li>
  )
}

interface SidebarFlyoutProps {
  id: string
  anchorRef: RefObject<HTMLButtonElement | null>
  title: string
  items: NavLinkItem[]
  pathname: string
  /** `true` when focus should go back to the group button (Escape). */
  onClose: (returnFocus: boolean) => void
}

/**
 * The collapsed group's children, portalled beside its button. Focus moves
 * to the first link; Escape closes and returns focus; a press or focus
 * outside closes it.
 */
function SidebarFlyout({ id, anchorRef, title, items, pathname, onClose }: SidebarFlyoutProps) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = `${id}-title`
  const close = useEffectEvent(onClose)

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    const surface = ref.current
    if (!anchor || !surface) return
    surface.querySelector<HTMLElement>('a')?.focus()
    return observeMovement(anchor, surface, () => {
      const rect = rectOf(anchor)
      const margin = 8
      const top = Math.max(
        margin,
        Math.min(rect.top - 4, window.innerHeight - surface.offsetHeight - margin)
      )
      surface.style.setProperty('--sidebar-flyout-top', `${top}px`)
      surface.style.setProperty('--sidebar-flyout-left', `${rect.left + rect.width + 12}px`)
    })
  }, [anchorRef])

  useEffect(() => {
    const release = pushEscape(() => {
      close(true)
      return true
    })
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return
      close(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      release()
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [anchorRef])

  return (
    <Portal>
      <div
        ref={ref}
        id={id}
        className="sidebar__flyout"
        onBlur={(event) => {
          const next = event.relatedTarget
          if (next && !event.currentTarget.contains(next) && next !== anchorRef.current) {
            onClose(false)
          }
        }}
      >
        <p id={titleId} className="sidebar__panel-title">
          {title}
        </p>
        <ul className="sidebar__list" aria-labelledby={titleId}>
          {items.map((child) => (
            <li key={child.id} className="sidebar__item">
              <SidebarLink item={child} pathname={pathname} isCompact={false} />
            </li>
          ))}
        </ul>
      </div>
    </Portal>
  )
}
